import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSettings } from '../contexts/SettingsContext';
import { useCookieConsent } from '../contexts/CookieConsentContext';

const GA_LOADER_SCRIPT_ID = 'ga4-gtag-loader';
const GA_INLINE_SCRIPT_ID = 'ga4-gtag-init';

/**
 * Injeta o Google Analytics (GA4) e rastreia page views apenas nas páginas
 * públicas do site. Visitas ao painel administrativo (/admin/*) nunca são
 * rastreadas.
 */
export default function GoogleAnalytics() {
  const location = useLocation();
  const { settings } = useSettings();
  const { consent } = useCookieConsent();
  const analytics = settings?.analytics;
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isFirstTrackedRoute = useRef(true);

  const measurementId = analytics?.googleAnalyticsId?.trim();
  // Cookie de analytics não é essencial (LGPD) — só carrega depois de consentimento explícito.
  const isActive = !isAdminRoute && !!analytics?.enabled && !!measurementId && consent.analytics;

  useEffect(() => {
    if (!isActive || !measurementId) return;

    let loader = document.getElementById(GA_LOADER_SCRIPT_ID) as HTMLScriptElement | null;
    const src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    if (!loader) {
      loader = document.createElement('script');
      loader.id = GA_LOADER_SCRIPT_ID;
      loader.async = true;
      document.head.appendChild(loader);
    }
    if (loader.src !== src) {
      loader.src = src;
    }

    let inline = document.getElementById(GA_INLINE_SCRIPT_ID);
    if (!inline) {
      inline = document.createElement('script');
      inline.id = GA_INLINE_SCRIPT_ID;
      document.head.appendChild(inline);
    }
    inline.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){ window.dataLayer.push(arguments); }
      window.gtag = gtag;
      gtag('js', new Date());
      gtag('config', '${measurementId}');
    `;

    isFirstTrackedRoute.current = true;
  }, [isActive, measurementId]);

  useEffect(() => {
    if (!isActive || typeof (window as any).gtag !== 'function') return;

    if (isFirstTrackedRoute.current) {
      isFirstTrackedRoute.current = false;
      return;
    }

    (window as any).gtag('event', 'page_view', {
      page_path: location.pathname + location.search
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, isActive]);

  return null;
}
